import React, { useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Wallet,
  Users,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { KpiCard } from '../components/ui/KpiCard';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { PeriodSelector } from '../components/ui/PeriodSelector';
import { useDashboardData } from '../hooks/useDashboardData';
import { formatBRL, formatBRLCompact } from '../utils/FormatDecimal';

const PIE_COLORS = [
  '#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

export default function DashboardPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const { data, loading, error } = useDashboardData(year, month);

  if (loading && !data) return <LoadingSpinner message="Carregando dashboard..." />;
  if (error) return <EmptyState title="Erro" description={error} />;
  if (!data) return <EmptyState title="Sem dados" description="Nenhum dado disponível" />;

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PeriodSelector year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
        {loading && <span className="text-xs text-gray-400">Atualizando...</span>}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <KpiCard
          title="Receita do Mês"
          value={formatBRLCompact(data.receita)}
          tooltip={formatBRL(data.receita)}
          icon={<TrendingUp className="h-5 w-5" />}
          color="green"
        />
        <KpiCard
          title="Despesas do Mês"
          value={formatBRLCompact(data.despesas)}
          tooltip={formatBRL(data.despesas)}
          icon={<TrendingDown className="h-5 w-5" />}
          color="red"
        />
        <KpiCard
          title="Lucro Líquido"
          value={formatBRLCompact(data.lucro)}
          tooltip={formatBRL(data.lucro)}
          icon={<DollarSign className="h-5 w-5" />}
          color={data.lucro >= 0 ? 'blue' : 'orange'}
        />
        <KpiCard
          title="Distribuição de Lucros"
          value={formatBRLCompact(data.distribuicaoLucros)}
          tooltip={formatBRL(data.distribuicaoLucros)}
          icon={<Users className="h-5 w-5" />}
          color="orange"
        />
        <KpiCard
          title="Saldo em Caixa"
          value={formatBRLCompact(data.saldoCaixa)}
          tooltip={formatBRL(data.saldoCaixa)}
          icon={<Wallet className="h-5 w-5" />}
          color="purple"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Bar Chart: Receitas vs Despesas */}
        <Card>
          <CardContent>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Receitas vs Despesas (6 meses)
            </h3>
            {data.monthlyHistory.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.monthlyHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value: any) => formatBRL(Number(value))} />
                  <Legend />
                  <Bar dataKey="receita" name="Receita" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="despesas" name="Despesas" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState title="Sem dados" description="Nenhum dado para o período" />
            )}
          </CardContent>
        </Card>

        {/* Pie Chart: Despesas por Categoria */}
        <Card>
          <CardContent>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Despesas por Categoria
            </h3>
            {data.despesasPorCategoria.length > 0 ? (
              <div className="flex flex-col items-center">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={data.despesasPorCategoria}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {data.despesasPorCategoria.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: any) => formatBRL(Number(value))} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 justify-center">
                  {data.despesasPorCategoria.map((item, i) => (
                    <div key={item.name} className="flex items-center gap-1.5 text-xs text-gray-600">
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                      />
                      <span className="truncate max-w-[120px]">{item.name}</span>
                      <span className="text-gray-400 font-mono">{formatBRL(item.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyState title="Sem despesas" description="Nenhuma despesa neste período" />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
